import type { LocationQuery, RouteLocationNormalizedGeneric } from 'vue-router'
import branch from '@/lib/branch'
import browser from '@/lib/browser'

import { APP_STORE_LINKS } from '@/lib/enums'

/**
 * Currently android has an issue with underscores in URLs.
 * It occasionally gets swapped to a special character rather than the underscore.
 * In order to support client versions with this bug it was requested that we modify our
 * share-receive flow to accept any special character between "share" and "id" so
 * that these links will still work.
 */
export const getShareIdFromRoute = (
  route: RouteLocationNormalizedGeneric
): string | undefined => {
  const shareKey = Object.keys(route.query).find((key) =>
    key.match(/^share[-_\W]*id$/)
  )
  if (shareKey) {
    const value = route.query[shareKey]
    if (typeof value === 'string') {
      return value
    }
  }
}

/**
 * Check if the route's `type` query param includes `group-location`.
 * Supports comma-separated values (e.g. `?type=group-location,other`).
 */
export const isGroupLocationShare = (route: RouteLocationNormalizedGeneric) => {
  const type = route.query.type
  if (typeof type !== 'string') {
    return false
  }
  return type
    .split(',')
    .map((t) => t.trim())
    .includes('group-location')
}

/**
 * Forward all query params except `type` (only used for GLS routing).
 */
export const stripTypeParam = (query: LocationQuery) =>
  Object.fromEntries(Object.entries(query).filter(([key]) => key !== 'type'))

/**
 * Open a Branch deep link to the share content, or fall back to the
 * appropriate app store link (or map route as a last resort).
 *
 * Lives here instead of @/lib/branch because branch.js is not yet TypeScript.
 */
export async function openShareBranchLink(
  shareId: string | undefined,
  queryLocation?: string
) {
  const app = 'fish'
  if (shareId && (await branch.isLoaded())) {
    branch.createAndOpenContentLink(shareId, queryLocation as never)
  } else {
    const linkKey = browser.isAndroid()
      ? `ANDROID_${app.toUpperCase()}`
      : `IOS_${app.toUpperCase()}`
    const link =
      APP_STORE_LINKS[linkKey as keyof typeof APP_STORE_LINKS] ?? `/${app}/map`
    browser.go(link)
  }
}
